import { useEffect, useState } from "react";
import TokenSelector from "./tokenSelector";
import BigNumber from "bignumber.js";
import { useAccount } from "wagmi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchPrice, getMinSell } from "../app/store";
import { selectAmount, selectBuyMode, selectPrice, setAmount, setBuyMode } from "../features/trade";
import SetMaxButton from "./setMax";
import { selectChain } from "../features/chain";
import { selectPlaceholder, selectToken, setPlaceholder, setToken } from "../features/token";
import BuyMode from "./buyMode";
import BuyButton from "./buyButton";
import SellButton from "./sellButton";
import { useTokenSiteText } from "./language";
import { tokens } from "./data";

export type ListButtonType = {
    idx: number;
    listName: string;
    listIcon: string;
    setListIdx: (arg0: number) => void;
    setOpenList: (arg0: boolean) => void;
}
const Trade = (): JSX.Element => {
    const { address } = useAccount();
    const dispatch = useAppDispatch();
    const chain = useAppSelector(selectChain);
    const token = useAppSelector(selectToken);
    const amount = useAppSelector(selectAmount);
    const buyMode = useAppSelector(selectBuyMode);
    const placeholder = useAppSelector(selectPlaceholder);
    const price = useAppSelector(selectPrice);
    const { text } = useTokenSiteText();
    const siteTokenSymbol = "FACE";

    useEffect(() => {
        dispatch(setAmount(''));
    }, [chain]);

    function processAmount(): string {
        return amount;
    }

    function formatPreviewValue(value: BigNumber): string {
        if (!value.isFinite() || value.lt(0)) return text.quoteUnavailable;
        const decimals = value.gte(1) ? 6 : 8;
        return value.toFormat(decimals).replace(/\.?0+$/, '');
    }

    function getPreview() {
        const amountValue = new BigNumber(amount || 0);
        const priceValue = new BigNumber(price || 0);
        if (!amountValue.isFinite() || amountValue.lte(0) || !priceValue.isFinite() || priceValue.lte(0)) {
            return { label: buyMode ? text.estimatedBuy : text.estimatedReceive, value: text.quoteUnavailable, symbol: buyMode ? siteTokenSymbol : tokens[token][0] };
        }
        if (buyMode) {
            return {
                label: text.estimatedBuy,
                value: formatPreviewValue(amountValue.div(priceValue)),
                symbol: siteTokenSymbol,
            };
        }
        return {
            label: text.estimatedReceive,
            value: formatPreviewValue(amountValue.times(priceValue).times(0.92)),
            symbol: tokens[token][0],
        };
    }

    const preview = getPreview();

    return (
        <div className="select-none flex flex-col items-center ">
            <p className="mb-1 w-full text-center font-ARM text-[10px] md:text-xs text-cyan-200/80">
                {buyMode ? `${text.spend} ${placeholder}` : `${text.sell} ${placeholder}`}
            </p>
            <div className="flex items-center">
                <div className="bg-[#2b2b2b] rounded-full flex items-start h-8 md:h-10 ">
                    <TokenSelector></TokenSelector>
                    <input value={processAmount()} type={'number'} placeholder={placeholder}
                        onChange={(e) => { dispatch(setAmount(e.target.value)); }}
                        min={buyMode ? 0.000001 : getMinSell()}
                        className=" outline-0 mt-1.5 md:mt-2 w-32 md:w-48 bg-[#2b2b2b] border border-[#2b2b2b] border-outline-none text-sm md:text-base text-white">
                    </input>
                    <SetMaxButton />
                </div>
            </div>
            <div className="mt-2 w-full max-w-[17rem] md:max-w-[21rem] border-t border-white/10 pt-2 flex items-start justify-between gap-3 font-ARM">
                <span className="text-[10px] md:text-xs text-cyan-100/70">{preview.label}</span>
                <strong className="max-w-[65%] break-words text-right text-xs md:text-sm text-white">
                    {preview.value} {preview.symbol}
                </strong>
            </div>
            <div className="flex items-center gap-3 md:gap-6 mt-3">
                <BuyButton />
                <BuyMode />
                <SellButton />
            </div>
        </div>
    );
}

export default Trade;
